import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Control, Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import moment from 'moment'
import {
    ObservationColumn,
    ObservationDraft,
    GateObservation,
    GateFeedColumnKey,
    SectionKey,
    Direction,
} from '../models/client/observation'
import { text } from '../styles/mixins/text'
import { PanelContainer } from './PanelContainer'
import { CollapseContentContainer, ContentContainer } from './ContentContainer'
import { Icon } from './Icon/index'
import { Spacer } from './Spacer'
import { utcToDisplayDate, utcToMilitaryDisplayTime, utcToTimezoneDate } from '../util/datetime'
import { color } from '../styles/color'
import { TRANSITION_TIME_FAST_MS } from '../styles/mixins/constants'
import { TextInput } from './TextInput'
import { Image } from './Image'
import { TimePicker } from './TimePicker'
import { DatePicker } from './DatePicker'

import { Select } from './Select'
import { ConnectedCarrierSelect } from './ConnectedCarrierSelect'
import { ConnectedVehicleTypeSelect } from './ConnectedVehicleTypeSelect'
import { ConnectedLoadTypeSelect } from './ConnectedLoadTypeSelect'
import { ConnectedChassisTypeSelect } from './ConnectedTrailerTypeSelect'
import { ConnectedColorSelect } from './ConnectedColorSelector'
import { STATE_ABBR_OPTIONS } from '../content/location'
import { TextArea } from './TextArea'
import { ApiError } from '../util/errors/ApiError'
import { useMessage } from '../hooks/useMessage'
import { useModal } from '../hooks/useModal'
import { ViewableImage } from './TableGateFeed'
import { selectSiteDirectory, selectSiteLoading } from '../store/sites'
import { isEqual } from '../util/isEqual'
import { BaseError } from '../util/errors/BaseError'
import { selectUserToken } from '../store/user'
import { metadataClient } from '../clients/MetadataClient'
import {
    selectLoadTypeMetadata,
    selectVehicleTypeMetadata,
    selectChassisTypeMetadata,
} from '../store/metadata'

const ValueInput = ({
    valueKey,
    control,
    isEditing,
    data,
    label,
    disabled,
    options,
    onValueChanged,
}: {
    valueKey: GateFeedColumnKey
    disabled?: boolean
    control: Control
    label?: string
    isEditing?: boolean
    data?: GateObservation
    options?: Array<any>
    onValueChanged?: (value: any) => void
}): JSX.Element => {
    switch (valueKey) {
        case 'DATE':
            return (
                <Controller
                    name="assetTimestamp"
                    control={control}
                    render={({ onChange, value }) => {
                        return (
                            <DatePicker
                                label={label}
                                onChange={(nextDate) => {
                                    onChange(nextDate)
                                }}
                                value={value as string}
                                testingKeys={['GateFeedDateInput', valueKey]}
                                timezone={data?.timezone}
                                disabled={!isEditing}
                            />
                        )
                    }}
                />
            )
        case 'TIME':
            return (
                <Controller
                    name="assetTimestamp"
                    control={control}
                    render={({ onChange, value }) => {
                        return (
                            <TimePicker
                                label={label}
                                onChange={(nextDate) => {
                                    onChange(nextDate)
                                }}
                                value={value as string}
                                placeholder="Select time"
                                testingKeys={['GateFeedTimeInput', valueKey]}
                                timezone={data?.timezone}
                                disabled={!isEditing || disabled}
                            />
                        )
                    }}
                />
            )
        case 'siteId':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    // defaultValue={data?.[0]?.id}
                    render={({ onChange, value }) => {
                        return (
                            <Select
                                placeholder="Site..."
                                options={options || []}
                                label={label}
                                value={value}
                                disabled={!isEditing || disabled}
                                onChange={(nextValue) => {
                                    onChange(nextValue)
                                    onValueChanged?.(nextValue)
                                }}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'gateId':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    // defaultValue={data?.[0]?.id}
                    render={({ onChange, value }) => {
                        return (
                            <Select
                                placeholder="Gate..."
                                options={options || []}
                                label={label}
                                value={value}
                                disabled={!isEditing || disabled}
                                onChange={(nextValue) => {
                                    onChange(nextValue)
                                    onValueChanged?.(nextValue)
                                }}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'assetDirection':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    render={({ onChange, value }) => {
                        let parsedVal: undefined | Direction
                        if (value) {
                            parsedVal = value === 'ingate' ? 'ingate' : 'outgate'
                        }
                        return (
                            <Select
                                placeholder="Direction..."
                                options={[
                                    {
                                        label: 'Ingate',
                                        value: 'ingate',
                                    },
                                    {
                                        label: 'Outgate',
                                        value: 'outgate',
                                    },
                                ]}
                                label={label}
                                value={parsedVal}
                                disabled={!isEditing || disabled}
                                onChange={(nextValue) => {
                                    onChange(nextValue)
                                }}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'assetType':
            return (
                <Controller
                    name="assetTypeId"
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <ConnectedLoadTypeSelect
                                label={label}
                                value={value as string}
                                onChange={onChange}
                                disabled={!isEditing || disabled}
                                placeholder="Select..."
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'vehicleCarrierName':
            return (
                <Controller
                    name="vehicleCarrierId"
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <ConnectedCarrierSelect
                                label={label}
                                onChange={(nextId) => {
                                    onChange(nextId || undefined)
                                }}
                                disabled={!isEditing || disabled}
                                value={value as string}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'vehicleType':
            return (
                <Controller
                    name="vehicleTypeId"
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <ConnectedVehicleTypeSelect
                                label={label}
                                value={value as string}
                                disabled={!isEditing || disabled}
                                onChange={onChange}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                                defaultFirst
                            />
                        )
                    }}
                />
            )
        case 'loadType':
            return (
                <Controller
                    name="loadTypeId"
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <ConnectedLoadTypeSelect
                                label={label}
                                value={value as string}
                                disabled={!isEditing || disabled}
                                onChange={onChange}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                                defaultFirst
                            />
                        )
                    }}
                />
            )
        // case 'chassisType':
        //     return (
        //         <Controller
        //             name="chassisTypeId"
        //             control={control}
        //             render={({ value, onChange }) => {
        //                 return (
        //                     <ConnectedChassisTypeSelect
        //                         label={label}
        //                         value={value as string}
        //                         disabled={!isEditing || disabled}
        //                         onChange={onChange}
        //                         testingKeys={['GateFeedInputSelect', valueKey]}
        //                         defaultFirst={true}
        //                     />
        //                 )
        //             }}
        //         />
        //     )
        case 'hasSeal':
            return (
                <Controller
                    name="hasSeal"
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <Select
                                label={label}
                                value={value ? 'Yes' : 'No'}
                                disabled={!isEditing || disabled}
                                options={[
                                    {
                                        label: 'Yes',
                                        value: 'Yes',
                                    },
                                    {
                                        label: 'No',
                                        value: 'No',
                                    },
                                ]}
                                onChange={(nextValue) => onChange(nextValue === 'Yes')}
                                testingKeys={['GateFeedInputSwitch', valueKey]}
                            />
                        )
                    }}
                />
            )
        // case 'vehicleColor':
        // case 'chassisColor':
        // case 'loadColor':
        // {
        //     let name = 'loadColorId'
        //     if (valueKey === 'chassisColor') name = 'chassisColorId'
        //     if (valueKey === 'vehicleColor') name = 'vehicleColorId'
        //     return (
        //         <Controller
        //             name={name}
        //             control={control}
        //             render={({ value, onChange }) => {
        //                 const safeValue = typeof value === 'string' ? value : null
        //                 return (
        //                     <ConnectedColorSelect
        //                         label={label}
        //                         placeholder="Select color..."
        //                         value={safeValue}
        //                         disabled={!isEditing || disabled}
        //                         onChange={(nextValue) => onChange(nextValue)}
        //                         testingKeys={['GateFeedInputSelect', valueKey]}
        //                     />
        //                 )
        //             }}
        //         />
        //     )
        // }
        // case 'loadState':
        // case 'chassisState':
        // case 'vehicleState':
        // return (
        //     <Controller
        //         name={valueKey}
        //         control={control}
        //         render={({ value, onChange }) => {
        //             return (
        //                 <Select
        //                     label={label}
        //                     placeholder="Select state..."
        //                     value={value as string}
        //                     options={[...STATE_ABBR_OPTIONS]}
        //                     onChange={onChange}
        //                     disabled={!isEditing || disabled}
        //                     testingKeys={['GateFeedInputSelect', valueKey]}
        //                 />
        //             )
        //         }}
        //     />
        // )

        case 'vehicleWeightClass':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <Select
                                label={label}
                                placeholder="Select class..."
                                value={value as string}
                                options={[
                                    {
                                        label: 'Class 1',
                                        value: '1',
                                    },
                                    {
                                        label: 'Class 2a',
                                        value: '2a',
                                    },
                                    {
                                        label: 'Class 2a',
                                        value: '2b',
                                    },
                                    {
                                        label: 'Class 3',
                                        value: '3',
                                    },
                                    {
                                        label: 'Class 4',
                                        value: '4',
                                    },
                                    {
                                        label: 'Class 5',
                                        value: '5',
                                    },
                                    {
                                        label: 'Class 6',
                                        value: '6',
                                    },
                                    {
                                        label: 'Class 7',
                                        value: '7',
                                    },
                                    {
                                        label: 'Class 8',
                                        value: '8',
                                    },
                                ]}
                                onChange={onChange}
                                disabled={!isEditing || disabled}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )

        case 'vehicleFuelType':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <Select
                                label={label}
                                placeholder="Select fuel type..."
                                value={value as string}
                                options={[
                                    {
                                        label: 'D - Diesel',
                                        value: 'D',
                                    },
                                    {
                                        label: 'NZE - Near Zero Emission',
                                        value: 'NZE',
                                    },
                                    {
                                        label: 'ZE - Zero Emission',
                                        value: 'ZE',
                                    },
                                ]}
                                onChange={onChange}
                                disabled={!isEditing || disabled}
                                testingKeys={['GateFeedInputSelect', valueKey]}
                            />
                        )
                    }}
                />
            )

        // NUMBER VALUES
        case 'loadDwellTimeHr':
        case 'chassisDwellTimeHr':
        case 'vehicleDwellTimeHr':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <TextInput
                                label={label}
                                type="number"
                                value={value as string}
                                onChange={onChange}
                                disabled
                                placeholder="Enter value..."
                                testingKeys={['GateFeedInputTextInput', valueKey]}
                            />
                        )
                    }}
                />
            )
        // TEXT AREA VALUES
        case 'vehicleRemarks':
        case 'chassisRemarks':
        case 'loadRemarks':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <TextArea
                                label={label}
                                disabled={!isEditing || disabled}
                                value={value as string}
                                onChange={onChange}
                                placeholder="Enter remarks..."
                                testingKeys={['GateFeedInputTextInput', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'sealNumber':
            return (
                <Controller
                    name="sealNumber"
                    // defaultValue=""
                    control={control}
                    render={({ value, onChange }) => {
                        return (
                            <TextInput
                                disabled={!isEditing || disabled}
                                value={value as string}
                                onChange={onChange}
                                placeholder="Enter value..."
                                testingKeys={['GateFeedInputTextInput', valueKey]}
                            />
                        )
                    }}
                />
            )
        // TEXT INPUT VALUES
        case 'loadDamage':
        case 'chassisDamage':
        case 'vehicleDamage':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    // defaultValue=""
                    render={({ value, onChange }) => {
                        return (
                            <TextInput
                                label={label}
                                value={value as string}
                                onChange={onChange}
                                disabled
                                placeholder="Enter value..."
                                testingKeys={['GateFeedInputTextInput', valueKey]}
                            />
                        )
                    }}
                />
            )
        case 'vehicleDotNumber':
        case 'vehicleNumber':
        case 'loadLicensePlate':
        case 'vehicleLicensePlate':
        case 'chassisLicensePlate':
        case 'chassisNumber':
        case 'loadNumber':
        case 'loadCustomerName':
            return (
                <Controller
                    name={valueKey}
                    control={control}
                    // defaultValue=""
                    render={({ value, onChange }) => {
                        return (
                            <TextInput
                                label={label}
                                value={value as string}
                                onChange={onChange}
                                disabled={!isEditing || disabled}
                                placeholder="Enter value..."
                                testingKeys={['GateFeedInputTextInput', valueKey]}
                            />
                        )
                    }}
                />
            )
        default:
            return <Spacer size="fill" />
    }
}

type Props = {
    template: ObservationColumn[]
    data?: GateObservation
    isDuplication?: boolean
    isNew?: boolean
    isReport?: boolean
    onSave?: (draft: ObservationDraft) => Promise<void>
    onDelete?: (id: string) => Promise<void>
    onOutgate?: (id: string, outgateDateTime: string) => Promise<void>
    onExit: () => void
    onViewImage: (image: ViewableImage) => void
}

const DEFAULT_STATE_OPEN = {
    ASSET: true,
    VEHICLE: true,
    CHASSIS: true,
    CONTAINER: true,
    OPERATIONS: true,
}

const DEFAULT_STATE_CLOSED = {
    ASSET: false,
    VEHICLE: false,
    CHASSIS: false,
    CONTAINER: false,
    OPERATIONS: false,
}

export const ObservationDetails = (props: Props): JSX.Element => {
    const {
        data,
        template,
        isDuplication,
        isNew,
        isReport,
        onSave,
        onDelete,
        onOutgate,
        onExit,
        onViewImage,
    } = props

    const [isEditing, setEditing] = useState(!!isDuplication || !!isNew)
    const [loading, setLoading] = useState<'saving' | 'deleting' | 'outgating' | null>(null)
    const messenger = useMessage()
    const [visibleSections, setVisibleSections] = useState<Record<SectionKey, boolean>>(
        isDuplication ? DEFAULT_STATE_OPEN : DEFAULT_STATE_CLOSED
    )
    const siteDirectory = useSelector(selectSiteDirectory, isEqual)

    const defaultSite = Object.values(siteDirectory || {})[0]
    const defaultGate = Object.values(defaultSite?.gates || {})[0]
    // setting defaults
    const chassisTypes = useSelector(selectChassisTypeMetadata, isEqual)
    const vehicleTypes = useSelector(selectVehicleTypeMetadata, isEqual)
    const loadTypes = useSelector(selectLoadTypeMetadata, isEqual)

    const [outgateDateTime, setOutgateDateTime] = useState(
        data?.assetTimestamp
            ? new Date(data?.assetTimestamp).toISOString()
            : new Date().toISOString()
    )
    const isEditOutgateTime = true
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { control, formState, watch, handleSubmit, reset } = useForm<ObservationDraft>({
        defaultValues: {
            siteId: defaultSite?.id || null,
            gateId: defaultGate?.id || null,
            assetTimestamp: data?.assetTimestamp || new Date().toISOString(),
            assetDirection: data?.assetDirection || null,
            assetTypeId: data?.assetTypeId || null,
            loadNumber: data?.loadNumber || null,
            hasSeal: /true|yes/i.test(`${data?.hasSeal}`), // FIXME: workaround for now to get the change out, think we need introduce a `ObservationRow` to handle Yes/No string values on table render
            sealNumber: data?.sealNumber || null,
            loadLicensePlate: data?.loadLicensePlate || null,
            // loadState: data?.loadState || null,
            loadTypeId: data?.loadTypeId || null,
            // loadColorId: data?.loadColorId || null,
            loadDamage: data?.loadDamage || null,
            loadDwellTimeHr: data?.loadDwellTimeHr || null,
            loadRemarks: data?.loadRemarks || null,
            loadCustomerName: data?.loadCustomerName || null,
            chassisTypeId: data?.chassisTypeId || null,
            chassisNumber: data?.chassisNumber || null,
            chassisDamage: data?.chassisDamage || null,
            chassisDwellTimeHr: data?.chassisDwellTimeHr || null,
            chassisLicensePlate: data?.chassisLicensePlate || null,
            // chassisState: data?.chassisState || null,
            // chassisColorId: data?.chassisColorId || null,
            chassisRemarks: data?.chassisRemarks || null,
            vehicleLicensePlate: data?.vehicleLicensePlate || null,
            vehicleNumber: data?.vehicleNumber || null,
            vehicleDotNumber: data?.vehicleDotNumber || null,
            // vehicleState: data?.vehicleState || null,
            vehicleCarrierId: data?.vehicleCarrierId || null,
            vehicleTypeId: data?.vehicleTypeId || null,
            // vehicleColorId: data?.vehicleColorId || null,
            vehicleDamage: data?.vehicleDamage || null,
            vehicleDwellTimeHr: data?.vehicleDwellTimeHr || null,
            vehicleRemarks: data?.vehicleRemarks || null,
            vehicleWeightClass: data?.vehicleWeightClass || null,
            vehicleFuelType: data?.vehicleFuelType || null,
        },
    })

    const [DeleteModal, triggerDeleteModal] = useModal({
        onCancel: () => {
            setLoading(null)
        },
        onConfirm: () => {
            console.log('confirm delete')
        },
    })
    const [OutgateModal, triggerOutgateModal] = useModal({
        onCancel: () => {
            setLoading(null)
        },
        onConfirm: async () => {
            console.log('confirm outgate')
            await onOutgate?.(data.observationId, outgateDateTime)
            setLoading(null)
        },
    })
    const handleDelete = async (): Promise<void> => {
        try {
            if (!data?.observationId)
                throw new ApiError({
                    type: 'BadRequest',
                    cause: new Error('ObservationEdit: Invalid ID'),
                })
            setLoading('deleting')
            await triggerDeleteModal(true)
            await onDelete?.(data.observationId)
            setLoading(null)
            onExit?.()
        } catch (e) {
            if (e instanceof Error) {
                messenger.error(e)
            }
        }
    }
    const handleOutgate = async (): Promise<void> => {
        try {
            if (!data?.observationId)
                throw new ApiError({
                    type: 'BadRequest',
                    cause: new Error('ObservationEdit: Invalid ID'),
                })
            setLoading('outgating')
            await triggerOutgateModal(true)
            onExit?.()
        } catch (e) {
            if (e instanceof Error) {
                messenger.error(e)
            }
        }
    }

    const submit = async (draft: ObservationDraft): Promise<void> => {
        if (draft.chassisNumber || draft.chassisLicensePlate) {
            draft.chassisTypeId = chassisTypes?.Default?.id || null
        }
        if (draft.loadNumber || draft.loadLicensePlate) {
            draft.loadTypeId = draft.loadTypeId || loadTypes?.Container?.id || null
        }
        draft.vehicleTypeId = draft.vehicleTypeId || vehicleTypes?.Tractor?.id || null
        setLoading('saving')
        await onSave?.(draft)
        setLoading(null)
        setEditing(false)
    }
    const images = data?.assetImageUrls || []
    const previewUrl = data?.assetPreviewUrl
    const imagePaths = data?.assetImagePaths
    const { isDirty } = formState
    const showCancel = isEditing && !isDuplication && !isNew
    const showEdit = !isEditing
    const hasChanges = isDuplication || isDirty
    const disableSave = loading === 'deleting' || !hasChanges

    let header = 'Gate Transaction'

    if (isDuplication) {
        header = 'Copy Transaction'
    }

    if (isNew) {
        if (isReport) {
            header = 'Add Asset'
        } else {
            header = 'Add Transaction'
        }
    }

    const disableAllMutatingActions = false

    const sites = Object.values(siteDirectory || {}).map((site) => {
        return site
    })

    const gates = Object.values(siteDirectory || {}).flatMap((site) => {
        return Object.values(site.gates || {}).map((gate) => {
            return gate
        })
    })

    const [selectedSiteId, setSelectedSiteId] = useState(data?.siteId || sites[0]?.id || null)
    const [selectedSiteGates, setSelectedSiteGates] = useState(gates)

    let isCanOutgate = false
    if (
        (data?.vehicleOutwardObservationId == null && data?.vehicleId != null) ||
        (data?.loadOutwardObservationId == null && data?.loadId !== null) ||
        (data?.chassisOutwardObservationId == null && data?.chassisId != null)
    ) {
        isCanOutgate = true
    }
    return (
        <PanelContainer
            onClose={() => onExit?.()}
            onCancel={
                showCancel
                    ? () => {
                          reset()
                          setEditing(false)
                      }
                    : undefined
            }
            header={header}
            onEdit={
                showEdit
                    ? () => {
                          setEditing(true)
                      }
                    : undefined
            }
            onSave={isEditing ? handleSubmit(submit) : undefined}
            onDelete={isDuplication ? undefined : handleDelete}
            onOutgate={
                data?.assetDirection === 'ingate' && isCanOutgate ? handleOutgate : undefined
            }
            disableSave={disableSave}
            disableEdit={disableAllMutatingActions || !!loading}
            disableDelete={disableAllMutatingActions || !!loading || !!isNew}
            disableOutgate={disableAllMutatingActions || !!loading || !!isNew}
            isDeleting={loading === 'deleting'}
        >
            <DeleteModal confirmLabel="Yes" cancelLabel="No">
                <div>
                    Are you sure you want to
                    <strong> delete </strong>
                    this record?
                </div>
            </DeleteModal>
            <OutgateModal confirmLabel="Yes" cancelLabel="No">
                <div>
                    Are you sure you want to
                    <strong> outgate </strong>
                    this record?
                    <DatePicker
                        label="Date"
                        onChange={(nextDate) => {
                            const newDate = moment(
                                data?.timezone
                                    ? utcToTimezoneDate(nextDate, data?.timezone)
                                    : nextDate
                            )
                            setOutgateDateTime(newDate.toISOString())
                            console.log(newDate.toISOString())
                            console.log(outgateDateTime)
                        }}
                        value={outgateDateTime}
                        timezone={data?.timezone}
                        disabled={false}
                    />
                    <TimePicker
                        label="Time"
                        onChange={(nextDate) => {
                            const newDate = moment(
                                data?.timezone
                                    ? utcToTimezoneDate(nextDate, data?.timezone)
                                    : nextDate
                            )
                            setOutgateDateTime(newDate.toISOString())
                            console.log(newDate.toISOString())
                            console.log(outgateDateTime)
                        }}
                        value={outgateDateTime}
                        placeholder="Select time"
                        timezone={data?.timezone}
                        disabled={false}
                    />
                </div>
            </OutgateModal>
            <ScrollBox>
                <ContentBox>
                    {isNew && (
                        <>
                            <Spacer size={2} vertical />
                            <Label>Site ID:</Label>
                            <ValueInput
                                control={control}
                                valueKey="siteId"
                                isEditing={isEditing}
                                data={data}
                                onValueChanged={(value) => {
                                    setSelectedSiteId(value)

                                    const selectedGates = (gates || []).filter((gate) => {
                                        return selectedSiteId ? gate.siteId === value : false
                                    })

                                    setSelectedSiteGates(selectedGates)

                                    control.setValue('gateId', selectedGates[0]?.id)
                                }}
                                options={(sites || []).map((site) => {
                                    return {
                                        label: site.name,
                                        value: site.id,
                                    }
                                })}
                            />
                        </>
                    )}

                    {isNew && (
                        <>
                            <Spacer size={2} vertical />
                            <Label>Gate ID:</Label>
                            <ValueInput
                                control={control}
                                valueKey="gateId"
                                isEditing={isEditing}
                                data={data}
                                options={(selectedSiteGates || []).map((gate) => {
                                    return {
                                        label: gate.name,
                                        value: gate.id,
                                    }
                                })}
                            />
                        </>
                    )}
                </ContentBox>

                <Spacer vertical size={2} />

                <ContentBox>
                    {!isNew && data?.assetImagePaths?.length ? (
                        <StyledImage
                            onClick={() => {
                                onViewImage({
                                    images: data?.assetImageUrls || [],
                                    path: data?.assetPreviewUrl || '',
                                    imagePaths: data?.assetImagePaths || [],
                                    observationId: data?.observationId || '',
                                })
                            }}
                            path={previewUrl || ''}
                            fileName={images[0]}
                            filePath={imagePaths?.[0]}
                            description="Truck preview"
                        />
                    ) : (
                        <div />
                    )}
                    <Spacer size={2} vertical />
                    {isNew ? <div /> : <Label>Recorded:</Label>}
                    {isNew ? (
                        <Controller
                            control={control}
                            name="assetTimestamp"
                            render={({ value, onChange }) => {
                                return (
                                    <Row>
                                        <DatePicker
                                            label="Date"
                                            timezone={data?.timezone}
                                            value={value as string}
                                            onChange={onChange}
                                        />
                                        <Spacer size={4} />
                                        <TimePicker
                                            label="Time"
                                            timezone={data?.timezone}
                                            value={value as string}
                                            onChange={onChange}
                                        />
                                    </Row>
                                )
                            }}
                        />
                    ) : (
                        <Row>
                            <Icon iconKey="Calendar" color="primary" size={3} />
                            <Spacer size={1} />
                            {data?.assetTimestamp &&
                                utcToDisplayDate(data.assetTimestamp, data?.timezone)}
                            <Spacer size={4} />
                            <Icon iconKey="Clock" color="primary" size={3} />
                            <Spacer size={1} />
                            {data?.assetTimestamp &&
                                utcToMilitaryDisplayTime(data.assetTimestamp, data?.timezone)}
                        </Row>
                    )}

                    <Spacer size={2} vertical />

                    {/*
                        <Label>Type:</Label>
                        <ValueInput
                            control={control}
                            valueKey="assetType"
                            isEditing={isEditing}
                            data={data}
                        />
                        */}

                    {!(isNew && isReport) && (
                        <>
                            <Spacer size={2} vertical />
                            <Label>Direction:</Label>
                            <ValueInput
                                valueKey="assetDirection"
                                control={control}
                                isEditing={isEditing}
                                data={data}
                            />
                        </>
                    )}
                </ContentBox>

                {template.map((group) =>
                    group.key === 'ASSET' || group.key === 'OPERATIONS' ? null : (
                        <div key={`group-${group.key}`}>
                            <Spacer vertical size={2} />
                            <CollapseContentContainer
                                key={group.key}
                                isOpen={visibleSections[group.key]}
                                onToggle={() => {
                                    setVisibleSections({
                                        ...visibleSections,
                                        [group.key]: !visibleSections[group.key],
                                    })
                                }}
                                title={group.label}
                            >
                                <>
                                    {group.columns.map(({ label, key }) =>
                                        key === 'EDIT_OPTIONS' ? (
                                            <div key={key} />
                                        ) : (
                                            <Column key={key}>
                                                <ValueInput
                                                    label={label}
                                                    valueKey={key}
                                                    control={control}
                                                    isEditing={isEditing}
                                                    disabled={
                                                        key === 'sealNumber'
                                                            ? !watch('hasSeal', false)
                                                            : undefined
                                                    }
                                                />
                                                <Spacer vertical size={2} />
                                            </Column>
                                        )
                                    )}
                                </>
                            </CollapseContentContainer>
                        </div>
                    )
                )}
            </ScrollBox>
            <Spacer size={2} vertical />
        </PanelContainer>
    )
}

const StyledImage = styled(Image)`
    height: 300px;
`

const ScrollBox = styled.div`
    display: flex;
    flex-flow: column nowrap;
    flex: 0 1 auto;
    overflow: auto;
    min-height: 0px;
`

const ContentBox = styled(ContentContainer)`
    width: 100%;
    flex-flow: column;
    flex: 0 0 auto;
`

const Label = styled.span`
    ${text('label', 'secondary', 'text', 'bold')}
`

const EditIcon = styled(Icon).attrs({
    iconKey: 'Edit',
})`
    opacity: 0;
    transition: all ${TRANSITION_TIME_FAST_MS}ms;
`

const Row = styled.div`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
`

const Column = styled.div<{ isEditable?: boolean }>`
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    width: 100%;
    ${text('paragraph', 'primary')}
    ${(p) =>
        p.isEditable &&
        css`
            &:hover {
                background-color: ${color.brand.muted};
                cursor: pointer;
            }
            &:hover ${EditIcon} {
                opacity: 1;
            }
            &:active {
                opacity: 0.5;
            }
        `}
`
